import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const smsQueryKeys = {
  all: ['sms'],
  lists: () => [...smsQueryKeys.all, 'list'],
  list: (params) => [...smsQueryKeys.lists(), params],
  history: (params) => [...smsQueryKeys.lists(), 'history', params],
};

// ==================== 연락처 목록 조회 ====================
/**
 * SMS 목록 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.Keyword - 검색어
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useContactList = (params = {}) => {
  return useQuery({
    queryKey: smsQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/drivesms/api/Contact', { params });
      return response;
    },
    enabled: true,
  });
};

// ==================== 연락처 삭제 ====================
/**
 * 연락처 삭제
 * @param {string} name - 이름
 * @param {string} phoneNumber - 전화번호
 * @param {string} organization - 조직
 */
export const useDeleteContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, phoneNumber, organization }) => {
      const response = await api.delete(`/drivesms/api/Contact`, {
        data: {
          name,
          phoneNumber,
          organization,
        }
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsQueryKeys.lists() });
    },
  });
};

// ==================== 연락처 추가 ====================
/**
 * 연락처 추가
 * @param {string} name - 이름
 * @param {string} phoneNumber - 전화번호
 * @param {string} organization - 조직
 */
export const useAddContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, phoneNumber, organization }) => {
      const response = await api.post(`/drivesms/api/Contact`, {
        name,
        phoneNumber,
        organization,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsQueryKeys.lists() });
    },
  });
};





// ==================== SMS 발송 ====================
/**
 * SMS 발송
 * @param {string} drType - DR 타입
 * @param {string} target - 대상
 * @param {string} dateInfo - 날짜 정보
 * @param {string} capacity - 용량
 */
export const useSendSms = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ drType, target, dateInfo, capacity }) => {
      const response = await api.post(`/drivesms/api/Sms`, {
        drType,
        target,
        dateInfo,
        capacity,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsQueryKeys.lists() });
    },
    retry: false, // SMS 발송은 실패 시 재시도하지 않음
  });
};

// ==================== SMS 발송 이력 조회 ====================
/**
 * SMS 발송 이력 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.Keyword - 검색어
 * @param {string} params.StartDate - 시작 날짜
 * @param {string} params.EndDate - 종료 날짜
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useSmsHistory = (params = {}) => {
  return useQuery({
    queryKey: smsQueryKeys.history(params),
    queryFn: async () => {
      const response = await api.get('/drivesms/api/Sms/histories', { params });
      return response;
    },
    enabled: true,
  });
};
